import api from './api';
import { config } from '../config';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  refreshToken: string;
  expiresAt: string;
  user: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
    isAdmin: boolean;
    lastLogin: string;
    createdAt: string;
    updatedAt?: string;
    lastPasswordChange?: string;
  };
}

export interface RefreshTokenRequest {
  refreshToken: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export const authService = {
  // Connexion
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await api.post(`${config.endpoints.auth}/login`, credentials);
    const data = response.data;
    
    // Stocker le token et le refresh token
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    
    return data;
  },

  // Déconnexion
  logout: async (): Promise<void> => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await api.post(`${config.endpoints.auth}/logout`, { refreshToken });
      }
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      // Nettoyer le localStorage
      localStorage.removeItem('authToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
    }
  },

  // Rafraîchir le token
  refreshToken: async (): Promise<LoginResponse> => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('Aucun refresh token disponible');
    }

    const response = await api.post(`${config.endpoints.auth}/refresh`, { refreshToken });
    const data = response.data;
    
    // Mettre à jour les tokens
    localStorage.setItem('authToken', data.token);
    localStorage.setItem('refreshToken', data.refreshToken);
    localStorage.setItem('user', JSON.stringify(data.user));
    
    return data;
  },

  // Vérifier si l'utilisateur est connecté
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('authToken');
    return !!token;
  },

  // Obtenir l'utilisateur actuel
  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  // Obtenir le token actuel
  getToken: (): string | null => {
    return localStorage.getItem('authToken');
  },

  // Changer le mot de passe
  changePassword: async (passwordData: ChangePasswordRequest): Promise<void> => {
    await api.post(`${config.endpoints.auth}/change-password`, passwordData);
  },

  // Vérifier la validité du token
  validateToken: async (): Promise<boolean> => {
    try {
      const response = await api.get(`${config.endpoints.auth}/validate`);
      return response.status === 200;
    } catch (error) {
      return false;
    }
  }
};
